'use client';

import React, { useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { SmartCaptchaDialogContext } from './smart-captcha-dialog-context';

const SITE_KEY =
  process.env.NEXT_PUBLIC_YANDEX_SMARTCAPTCHA_SITE_KEY ||
  'ysc1_K3tNL9H4J9rdIwXtXooRgVVUcGAfDh8iYaT3tyYC4c730f34';

const SCRIPT_SRC =
  'https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload&onload=onYandexSmartCaptchaLoad';

export const SMARTCAPTCHA_LOAD_TIMEOUT_MS = 10_000;

type SmartCaptchaApi = {
  render: (
    containerId: string,
    options: {
      sitekey: string;
      invisible: boolean;
      hl?: 'ru' | 'en';
      test?: boolean;
      callback: (token: string) => void;
      hideShield?: boolean;
      shieldPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    }
  ) => number;
  execute: (widgetId?: number) => void;
  reset: (widgetId?: number) => void;
  destroy: (widgetId: number) => void;
  subscribe: (
    widgetId: number,
    event: 'token-expired' | 'network-error' | 'javascript-error' | 'challenge-visible' | 'challenge-hidden',
    callback: () => void,
  ) => () => void;
};

declare global {
  interface Window {
    smartCaptcha?: SmartCaptchaApi;
    onYandexSmartCaptchaLoad?: () => void;
    __yandexSmartCaptchaReady?: Promise<void>;
    __resolveYandexSmartCaptchaReady?: () => void;
    __rejectYandexSmartCaptchaReady?: (error: Error) => void;
  }
}

/** Visible verification for forms inside a Radix modal. */
export function VisibleSmartCaptcha({ onTokenChange, onChallengeChange }: {
  onTokenChange: (token: string) => void;
  onChallengeChange: (visible: boolean) => void;
}) {
  const reactId = useId();
  const containerId = `visible-smart-captcha-${reactId.replace(/:/g, '')}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const { setChallengeVisible } = useContext(SmartCaptchaDialogContext);
  const challengeChange = useCallback((visible: boolean) => {
    onChallengeChange(visible);
    setChallengeVisible(visible);
  }, [onChallengeChange, setChallengeVisible]);

  useEffect(() => {
    let cancelled = false;
    let widgetId: number | null = null;
    const unsubscribers: (() => void)[] = [];
    onTokenChange('');
    setError('');
    setIsReady(false);

    loadSmartCaptchaScript().then(() => {
      const api = window.smartCaptcha;
      const container = containerRef.current;
      if (cancelled || !api || !container) return;

      const testMode = process.env.NODE_ENV !== 'production' &&
        new URLSearchParams(window.location.search).get('captchaTest') === '1';
      widgetId = api.render(containerId, {
        sitekey: SITE_KEY,
        invisible: testMode,
        hl: 'ru',
        test: testMode,
        callback: (token) => {
          if (!cancelled) {
            setError('');
            onTokenChange(token);
          }
        },
      });

      // Leave Yandex's iframe in its original DOM position. Moving a mounted
      // iframe reloads its document and can break the challenge's message bridge.
      unsubscribers.push(api.subscribe(widgetId, 'challenge-visible', () => challengeChange(true)));
      unsubscribers.push(api.subscribe(widgetId, 'challenge-hidden', () => challengeChange(false)));
      unsubscribers.push(api.subscribe(widgetId, 'token-expired', () => onTokenChange('')));
      const handleError = () => {
        onTokenChange('');
        challengeChange(false);
        setError('Не удалось выполнить проверку. Попробуйте ещё раз.');
      };
      unsubscribers.push(api.subscribe(widgetId, 'network-error', handleError));
      unsubscribers.push(api.subscribe(widgetId, 'javascript-error', handleError));
      setIsReady(true);
      if (testMode) api.execute(widgetId);
    }).catch((cause) => {
      if (!cancelled) setError(cause instanceof Error ? cause.message : 'Не удалось загрузить капчу.');
    });

    return () => {
      cancelled = true;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      if (widgetId !== null) window.smartCaptcha?.destroy(widgetId);
      onTokenChange('');
      challengeChange(false);
    };
  }, [attempt, containerId, onTokenChange, challengeChange]);

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-600">Подтвердите, что вы не робот</p>
      <div ref={containerRef} id={containerId} className="min-h-[118px]" />
      {!isReady && !error && <p role="status" className="text-sm text-slate-500">Загружаем капчу…</p>}
      {error && (
        <div role="alert" className="text-sm text-red-700">
          {error}{' '}
          <button type="button" className="underline" onClick={() => setAttempt((value) => value + 1)}>
            Повторить проверку
          </button>
        </div>
      )}
    </div>
  );
}

function loadSmartCaptchaScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('SmartCaptcha is available only in browser'));
  }

  if (window.smartCaptcha) {
    return Promise.resolve();
  }

  if (!window.__yandexSmartCaptchaReady) {
    window.__yandexSmartCaptchaReady = new Promise<void>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)?.remove();
        window.__yandexSmartCaptchaReady = undefined;
        window.__resolveYandexSmartCaptchaReady = undefined;
        window.__rejectYandexSmartCaptchaReady = undefined;
        reject(new Error('SmartCaptcha не загрузилась вовремя. Попробуйте ещё раз.'));
      }, SMARTCAPTCHA_LOAD_TIMEOUT_MS);

      window.__resolveYandexSmartCaptchaReady = () => {
        window.clearTimeout(timeoutId);
        resolve();
      };
      window.__rejectYandexSmartCaptchaReady = (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      };
      window.onYandexSmartCaptchaLoad = window.__resolveYandexSmartCaptchaReady;
    });
  }

  if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.defer = true;
    script.async = true;
    script.onerror = () => {
      window.__rejectYandexSmartCaptchaReady?.(
        new Error('Не удалось загрузить SmartCaptcha. Проверьте соединение и повторите попытку.')
      );
      window.__yandexSmartCaptchaReady = undefined;
      script.remove();
    };
    document.head.appendChild(script);
  }

  return window.__yandexSmartCaptchaReady;
}

/** Shared visible verification; the token is consumed once by a form submission. */
export function useSmartCaptcha() {
  const tokenRef = useRef('');
  const [isCaptchaReady, setIsCaptchaReady] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [challengeVisible, setChallengeVisible] = useState(false);
  const onTokenChange = useCallback((token: string) => { tokenRef.current = token; setIsCaptchaReady(Boolean(token)); }, []);
  const executeCaptcha = useCallback(async () => {
    const token = tokenRef.current;
    if (!token) throw new Error('Подтвердите, что вы не робот.');
    tokenRef.current = '';
    setIsCaptchaReady(false);
    return token;
  }, []);
  const resetCaptcha = useCallback(() => {
    tokenRef.current = '';
    setIsCaptchaReady(false);
    setChallengeVisible(false);
    setAttempt((value) => value + 1);
  }, []);

  return {
    captchaElement: <VisibleSmartCaptcha key={attempt} onTokenChange={onTokenChange} onChallengeChange={setChallengeVisible} />,
    executeCaptcha,
    resetCaptcha,
    isCaptchaReady,
    challengeVisible,
  };
}
